<template>
  <div class="product-card swiper-slide-transform h-[550px] border border-[color:var(--color-border)] md:h-[505px]">
    <div class="product-card__image relative">
      <VcImage
        :src="product.imgSrc || ''"
        :alt="product.name"
        size-suffix="md"
        class="w-full rounded-xl object-contain object-center p-2"
        lazy
      />
    </div>

    <div class="product-card__content relative flex flex-col justify-between">
      <div>
        <router-link
          :to="link"
          :title="product.name"
          class="mb-4 line-clamp-2 text-2xl font-medium"
          @click="$emit('link-click', $event)"
        >
          {{ product.name }}
        </router-link>
        <div v-if="product.description?.content" class="product-card__description">
          {{ product.description?.content }}
        </div>
      </div>

      <div class="mt-2 border-t border-[color:var(--color-border)] pt-6">
        <VcItemPrice :value="product.price"></VcItemPrice>

        <AddToCart :product="product" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { getProductRoute } from "@/core/utilities";
import { AddToCart } from "@/shared/cart";
import type { Product } from "@/core/api/graphql/types";
import type { PropType } from "vue";
import type { RouteLocationRaw } from "vue-router";

defineEmits<{ (eventName: "link-click", globalEvent: PointerEvent): void }>();

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
});

const link = computed<RouteLocationRaw>(() => getProductRoute(props.product.id, props.product.slug));
</script>

<style lang="scss">
.product-card {
  @apply bg-white flex flex-col rounded-xl p-6;

  &__image {
    display: flex;
    justify-content: center;
    height: 50%;

    @media (max-width: 768px) {
      height: 50%;
    }
  }

  &__content {
    height: 60%;

    @media (max-width: 768px) {
      height: 50%;
    }
  }

  // Product Card Description
  .product-card__description {
    @apply line-clamp-2;

    @media (max-width: 768px) {
      @apply line-clamp-1;
    }
  }
}

// Root Carousel Style
.inno-carousel {
  &__wrapper::after {
    content: "";
    background: #fff;
    width: calc(50% - 50px);
    height: 505px;
    position: absolute;
    z-index: 0;
    left: 0;
    top: 0;
    border-radius: 12px;
  }

  // Swiper Slides
  .swiper-slide {
    width: calc(25% - 30px);
    height: 505px;

    &:last-child {
      margin-right: calc(25% + 30px);
    }

    &-prev {
      width: 50%;
      height: 505px;
    }

    &-active {
      width: 50%;
      height: 505px;

      .product-card__content {
        height: 100%;
      }

      .product-card__description {
        @apply line-clamp-6;

        @media (max-width: 768px) {
          @apply line-clamp-1;
        }
      }
    }
  }

  // Product Cards for Inactive Slides
  .swiper-slide:not(.swiper-slide-active) {
    .product-card {
      @apply bg-white flex flex-col rounded-xl;

      &__image {
        display: flex;
        justify-content: center;
        height: 40%;

        @media (max-width: 768px) {
          height: 50%;
        }
      }

      &__content {
        height: 60%;

        @media (max-width: 768px) {
          padding: 30px;
          height: 50%;
        }
      }
    }
  }

  // Product Cards for Active and Previous Slides
  .swiper-slide-prev,
  .swiper-slide-active {
    .product-card {
      @apply flex flex-row min-h-full items-stretch justify-between m-0 rounded-xl bg-white;

      @media (max-width: 768px) {
        @apply flex-col;
      }

      &__image {
        display: flex;
        justify-content: center;
        @apply w-1/2;

        @media (max-width: 768px) {
          @apply w-full h-1/2;
        }

        & img {
          @media (min-width: 768px) {
            height: 505px;
          }
        }
      }

      &__content {
        padding: 30px;
        padding-bottom: 0;

        @media (min-width: 768px) {
          @apply w-1/2;
        }
      }
    }
  }
}
</style>
